import React, { FC } from 'react';
import { Button, Modal } from '@vadiun/react-components';
import { Field, Form, Formik } from 'formik';
import { TextField, Select } from 'formik-material-ui';
import { FormControl, InputLabel, MenuItem } from '@material-ui/core';
import * as Yup from 'yup';
import paymentMethodsService from '../service/paymentMethodsService';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import {
  faArrowDown,
  faDollarSign,
  faHandHolding
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ManualPaymentFormData {
  payment_method_id: number;
  transaction_number: string | null;
  notes: string | null;
}

interface ManualPaymentModalProps {
  travelId: number;
  isOpen: boolean;
  amount: number;
  onClose: () => void;
  handleManualPayment: (
    travelId: number,
    payment_method_id: number,
    transaction_number: string | null,
    notes: string | null
  ) => void;
}
const baseValidationSchema = {
  payment_method_id: Yup.number().moreThan(0, 'El método de pago es requerido'),
  transaction_number: Yup.string().min(3, 'Mínimo 3 caracteres'),
  notes: Yup.string().nullable()
};

const manualPaymentSchema = Yup.lazy((values: ManualPaymentFormData) => {
  return Yup.object().shape({
    ...baseValidationSchema,
    transaction_number:
      values.payment_method_id === 1
        ? Yup.string().required('El número de transferencia es requerido')
        : Yup.string().nullable()
  });
});

const ManualPaymentModal: FC<ManualPaymentModalProps> = ({
  travelId,
  isOpen,
  amount,
  onClose,
  handleManualPayment
}) => {
  const usePaymentMethodsService = paymentMethodsService();
  const paymentMethods =
    useSuperQuery(usePaymentMethodsService.getMethodPayment).data ?? [];

  const initialValues: ManualPaymentFormData = {
    payment_method_id: 1,
    transaction_number: '',
    notes: ''
  };
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      body={
        <div className="relative">
          <div className="text-center mb-6 mt-10">
            <div className="flex items-center justify-center gap-3 mb-2 relative">
              <div
                className="rounded-full flex items-center justify-center"
                style={{
                  position: 'absolute',
                  top: '-67px',
                  right: '190px',
                  width: '130px',
                  height: '80px'
                }}
              >
                <FontAwesomeIcon
                  icon={faHandHolding}
                  className="text-white text-7xl"
                  style={{
                    color: '#05CC00'
                  }}
                />
              </div>
              <div
                className="rounded-full flex items-center justify-end pr-2"
                style={{
                  boxShadow: '4px -4px 4px rgba(0, 255, 17, 0.6)',
                  position: 'absolute',
                  top: '-78px',
                  right: '214px',
                  width: '60px',
                  height: '60px'
                }}
              >
                <FontAwesomeIcon
                  icon={faArrowDown}
                  className="text-white text-lg"
                  style={{
                    color: '#05CC00'
                  }}
                />
              </div>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  position: 'absolute',
                  top: '-73px',
                  right: '225px'
                }}
              >
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="text-white text-3xl"
                  style={{
                    color: '#05CC00'
                  }}
                />
              </div>
            </div>
            <h2
              className="text-2xl font-bold  mt-6"
              style={{
                color: '#00521E',
                textShadow: '1px 1px 2px rgba(0, 255, 0, 0.4)'
              }}
            >
              Método de Pago Manual
            </h2>
            <p className="text-gray-600 mt-1">
              Ingrese los datos del medio de pago que se efectuó
            </p>
          </div>
          <p className="text-gray-600 pl-4 mb-4" >
            Monto: <strong style={{ color: '#00521E' }}>${amount}</strong>
          </p>
          <Formik
            initialValues={initialValues}
            validationSchema={manualPaymentSchema}
            onSubmit={(values) => {
              handleManualPayment(
                travelId,
                values.payment_method_id,
                values.transaction_number,
                values.notes
              );
              onClose();
            }}
          >
            {({ dirty, touched, errors, isSubmitting, submitCount }) => (
              <Form className="flex flex-col gap-4 p-4">
                <FormControl
                  variant="outlined"
                  fullWidth
                  error={
                    !!errors.payment_method_id &&
                    (touched.payment_method_id || submitCount > 0)
                  }
                >
                  <InputLabel>Método de Pago</InputLabel>
                  <Field
                    component={Select}
                    name="payment_method_id"
                    label="Método de Pago"
                  >
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {method.method_name}
                      </MenuItem>
                    ))}
                  </Field>
                  {errors.payment_method_id &&
                    (touched.payment_method_id || submitCount > 0) && (
                      <div className="text-red-500 text-sm mt-1">
                        {errors.payment_method_id}
                      </div>
                    )}
                </FormControl>

                <Field
                  name="transaction_number"
                  label="Número de Transferencia"
                  variant="outlined"
                  component={TextField}
                  fullWidth
                />

                <Field
                  name="notes"
                  label="Notas"
                  variant="outlined"
                  component={TextField}
                  fullWidth
                />

                <div className="flex justify-between gap-2 pt-4" style={{
                  marginBottom: '-30px'
                }}>
                  <Button
                    type="button"
                    variant="light"
                    color="gray"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !dirty}
                  >
                    Guardar
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      }
    />
  );
};

export default ManualPaymentModal;
